import { z } from 'zod';

/**
 * Entity readiness testing (build update 2.4). The plan schema is shared with the
 * acceptance-test repo (legends-acceptance-tests), which runs each case with its
 * Playwright template and posts results back.
 */

export const ReadinessLayer = z.enum(['L1', 'L2', 'L3', 'L4']);
export type ReadinessLayer = z.infer<typeof ReadinessLayer>;

export const ReadinessEnv = z.enum(['TEST', 'PROD']);
export type ReadinessEnv = z.infer<typeof ReadinessEnv>;

export const ReadinessCase = z.object({
  /** e.g. "V046-P2P-01-BUY-4000" */
  id: z.string().min(1),
  layer: ReadinessLayer,
  /** e.g. "T-P2P-01" */
  template: z.string().min(1),
  title: z.string().min(1),
  /** Persona key ("buyer"); null for OData-only layers */
  persona: z.string().nullable(),
  company: z.string().min(1).max(4),
  data: z.record(z.union([z.string(), z.number()])),
  expect: z.record(z.unknown()),
  dependsOn: z.array(z.string()).optional(),
  envs: z.array(ReadinessEnv).min(1),
  /** Seeded demo case (section 15): never part of a real run. */
  demo: z.boolean().optional(),
});
export type ReadinessCase = z.infer<typeof ReadinessCase>;

export const ReadinessPlan = z.object({
  requestId: z.string().nullable(),
  company: z.string().min(1).max(4),
  generatedAt: z.string(),
  templateVersion: z.string(),
  cases: z.array(ReadinessCase),
});
export type ReadinessPlan = z.infer<typeof ReadinessPlan>;

export const ReadinessResultStatus = z.enum(['passed', 'failed', 'skipped']);
export type ReadinessResultStatus = z.infer<typeof ReadinessResultStatus>;

export const FailureKind = z.enum(['UiChange', 'Behavior', 'Data', 'Environment']);
export type FailureKind = z.infer<typeof FailureKind>;

/** One case result as the runner posts it (results.json). */
export const ReadinessCaseResult = z.object({
  caseId: z.string(),
  status: ReadinessResultStatus,
  durationMs: z.number().int().nonnegative(),
  errorSummary: z.string().nullable().optional(),
  failureKind: FailureKind.nullable().optional(),
  traceUrl: z.string().nullable().optional(),
  screenshotUrl: z.string().nullable().optional(),
});
export type ReadinessCaseResult = z.infer<typeof ReadinessCaseResult>;

// ── template catalog (section 5) ─────────────────────────────────────────────

export interface ReadinessTemplate {
  id: string;
  layer: ReadinessLayer;
  /** Who runs it, in words */
  persona: string;
  what: string;
  passesWhen: string;
  /** Module that switches the template on; null = always */
  module: 'Procurement' | 'AP' | 'Projects' | null;
  owner: string;
  /** In the R1 build of the acceptance-test repo */
  built: boolean;
}

export const READINESS_TEMPLATES: readonly ReadinessTemplate[] = [
  {
    id: 'T-CONF-01',
    layer: 'L1',
    persona: 'none (OData)',
    what: 'Compare one governed setting with the template',
    passesWhen: 'No unexpected differences',
    module: null,
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-LOAD-01',
    layer: 'L4',
    persona: 'none (OData)',
    what: 'Count copied records per entity group',
    passesWhen: 'Counts match the template, minus exclusions',
    module: null,
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-OVL-01',
    layer: 'L1',
    persona: 'none (OData)',
    what: 'Ledger overlay: a main account override (suspended, default dimension) in this company and in the template',
    passesWhen: 'Set in this company only; the template company and the shared chart unchanged',
    module: null,
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-OVL-02',
    layer: 'L3',
    persona: 'GL accountant',
    what: "Ledger overlay: post a journal line to an account outside the new journal name's range",
    passesWhen: 'Journal control blocks the line; an account inside the range posts',
    module: null,
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-ACC-01',
    layer: 'L2',
    persona: 'each role',
    what: "Open each form in the role's form list for this company",
    passesWhen: 'Every form loads with no "no access" message',
    module: null,
    owner: 'RSM D365 team',
    built: true,
  },
  {
    id: 'T-ACC-02',
    layer: 'L2',
    persona: 'each role',
    what: 'Company picker and a cross-company URL (?cmp=V001)',
    passesWhen: 'Only this company (and shared ones) available; other venues refused',
    module: null,
    owner: 'RSM D365 team',
    built: true,
  },
  {
    id: 'T-ACC-03',
    layer: 'L2',
    persona: 'buyer',
    what: 'Negative: try to post a general journal',
    passesWhen: 'Blocked by security',
    module: 'Procurement',
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-ACC-04',
    layer: 'L2',
    persona: 'none (OData)',
    what: "Role parity: every real user's role set equals one tested persona's set",
    passesWhen: 'All users covered',
    module: null,
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-P2P-01',
    layer: 'L3',
    persona: 'buyer',
    what: 'Create a PO (template vendor, procurement category, amount from the plan), submit to workflow',
    passesWhen: 'PO is "In review"',
    module: 'Procurement',
    owner: 'RSM D365 team',
    built: true,
  },
  {
    id: 'T-P2P-02',
    layer: 'L3',
    persona: 'none (OData or UI)',
    what: 'Read the workflow work item for that PO',
    passesWhen: 'Assigned to the persona seated in the expected seat',
    module: 'Procurement',
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-P2P-03',
    layer: 'L3',
    persona: 'approver seat',
    what: 'Approve from the action center (work items assigned to me)',
    passesWhen: 'PO is "Approved", then confirmed',
    module: 'Procurement',
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-AP-01',
    layer: 'L3',
    persona: 'AP clerk',
    what: 'Invoice the confirmed PO, submit to workflow',
    passesWhen: 'Invoice routed as the vendor invoice workflow says',
    module: 'AP',
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-AP-02',
    layer: 'L3',
    persona: 'AP approver',
    what: 'Approve the invoice',
    passesWhen: 'Invoice approved',
    module: 'AP',
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-GL-01',
    layer: 'L3',
    persona: 'GL approver',
    what: 'Approve a submitted journal (the journal is created in setup)',
    passesWhen: 'Journal approved',
    module: null,
    owner: 'RSM D365 team',
    built: false,
  },
  {
    id: 'T-PRJ-01',
    layer: 'L3',
    persona: 'project manager',
    what: "Create a project in the venue's project group",
    passesWhen: 'Project created with the venue dimension',
    module: 'Projects',
    owner: 'RSM D365 team',
    built: false,
  },
];

// ── test personas (section 6) ────────────────────────────────────────────────

/**
 * One synthetic test account per venue seat. Each persona gets the role bundle of the
 * person in that seat (from the people import), scoped to the venue company, and is
 * seated in that position in TEST only. Accounts: `rdy.<key>@<test domain>` (VERIFY V-R2).
 */
export interface ReadinessPersona {
  key: string;
  /** Short form used in case IDs (BUY, FM…) */
  short: string;
  label: string;
  /** The venue job whose seat the persona takes */
  jobId: string;
  /** Position suffix (V046-BUY) */
  seat: string;
}

export const READINESS_PERSONAS: readonly ReadinessPersona[] = [
  { key: 'gm', short: 'GM', label: 'General manager', jobId: 'VENUE-GM', seat: 'GM' },
  { key: 'fm', short: 'FM', label: 'Finance manager', jobId: 'FIN-MGR', seat: 'FM' },
  { key: 'buyer', short: 'BUY', label: 'Buyer', jobId: 'BUYER', seat: 'BUY' },
  { key: 'ap', short: 'AP', label: 'AP clerk', jobId: 'AP-CLERK', seat: 'AP' },
  { key: 'pm', short: 'PM', label: 'Project manager', jobId: 'PROJ-MGR', seat: 'PM' },
];

export const personaAccount = (key: string): string => `rdy.${key}@test.legends.example`;

/**
 * Forms each role must open (T-ACC-01). VERIFY V-R3: the real list per role and its
 * menu item names, recorded in TEST.
 */
export const ROLE_FORMS: Readonly<Record<string, readonly string[]>> = {
  LEDGERACCOUNTINGMANAGER: ['General journals', 'Main accounts', 'Trial balance'],
  LEDGERACCOUNTANT: ['General journals', 'Main accounts'],
  VENDPURCHASINGAGENT: ['Purchase orders', 'Purchase requisitions', 'Vendors'],
  VENDINVOICEACCOUNTSPAYABLECLERK: ['Pending vendor invoices', 'Vendor invoice journal', 'Vendors'],
  VENDINVOICEACCOUNTSPAYABLEMANAGER: ['Pending vendor invoices', 'Vendor payments', 'Vendor aging report'],
  PROJPROJECTMANAGER: ['All projects', 'Project groups', 'Project transactions'],
  LEDGERFINANCIALCONTROLLER: ['General journals', 'Financial reports', 'Period close workspace'],
};

// ── setup data (section 7) ───────────────────────────────────────────────────

export interface SetupDataItem {
  key: string;
  need: string;
  source: string;
  check: string;
}

export const READINESS_SETUP_DATA: readonly SetupDataItem[] = [
  {
    key: 'vendor',
    need: 'Vendor for POs and invoices',
    source: 'Template vendor TST-0001, copied to every venue',
    check: 'Exists in the company, not on hold',
  },
  {
    key: 'category',
    need: 'Procurement category',
    source: 'Shared category hierarchy',
    check: 'Exists and is active',
  },
  {
    key: 'numberSequences',
    need: 'Number sequences for PO, invoice, journal, project',
    source: 'Copy into legal entity',
    check: 'Each reference has a sequence',
  },
  {
    key: 'journal',
    need: 'Journal for T-GL-01',
    source: 'Created by the setup step through OData, unposted',
    check: 'Created this run',
  },
  { key: 'projectGroup', need: 'Project group', source: 'Template', check: 'Exists' },
  {
    key: 'personas',
    need: 'Personas seated, roles assigned',
    source: 'TEST seating overlay (section 6)',
    check: 'T-ACC-04 passes first',
  },
];

/** Readiness data set per venue archetype, versioned with the template. */
export const READINESS_DATA_SETS: ReadonlyArray<{ archetype: string; version: string; vendor: string }> = [
  { archetype: 'Arena', version: 'LTPL-rdy-1', vendor: 'TST-0001' },
  { archetype: 'Stadium', version: 'LTPL-rdy-1', vendor: 'TST-0001' },
  { archetype: 'Convention center', version: 'LTPL-rdy-1', vendor: 'TST-0001' },
];
